import { oauthReturnPath } from "@/lib/oauthReturnPath";
import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const emailSchema = z.string().trim().min(1, "Email is required").email("Enter a valid email address").max(255);
const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
});

interface Props {
  initialEmail: string;
  returnTo: string | null;
}

export function SignInForm({ initialEmail, returnTo }: Props) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<"email" | "password", string>>>({});
  const [loading, setLoading] = useState(false);

  const attemptSignIn = async () => {
    setAuthError(null);
    const parsed = signInSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setErrors({ email: fe.email?.[0], password: fe.password?.[0] });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) { setAuthError(error.message); return; }
    navigate(returnTo ? oauthReturnPath(returnTo, "/dashboard") : "/dashboard");
  };

  const forgot = async () => {
    setAuthError(null);
    const parsedEmail = emailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setErrors((p) => ({ ...p, email: parsedEmail.error.flatten().formErrors[0] }));
      return;
    }
    setErrors((p) => ({ ...p, email: undefined }));
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return toast.error(error.message);
    toast.success("Check your email for a reset link");
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); void attemptSignIn(); }} className="space-y-4" noValidate>
      {authError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to sign in</AlertTitle>
          <AlertDescription>{authError}</AlertDescription>
        </Alert>
      )}
      <div>
        <Label htmlFor="signin-email">Email</Label>
        <Input id="signin-email" type="email" autoComplete="email" required
          value={email}
          onChange={(e) => { setEmail(e.target.value); setAuthError(null); setErrors((p) => ({ ...p, email: undefined })); }}
          aria-invalid={errors.email ? "true" : "false"} />
        {errors.email && <p className="mt-1 text-sm text-destructive">{errors.email}</p>}
      </div>
      <div>
        <Label htmlFor="signin-password">Password</Label>
        <Input id="signin-password" type="password" autoComplete="current-password" required
          value={password}
          onChange={(e) => { setPassword(e.target.value); setAuthError(null); setErrors((p) => ({ ...p, password: undefined })); }}
          aria-invalid={errors.password ? "true" : "false"} />
        {errors.password && <p className="mt-1 text-sm text-destructive">{errors.password}</p>}
      </div>
      <Button type="submit" className="w-full" disabled={loading}>{loading ? "Signing in…" : "Sign in"}</Button>
      <button type="button" onClick={forgot} className="text-sm text-muted-foreground hover:text-foreground block mx-auto">Forgot password?</button>
      <p className="text-xs text-center text-muted-foreground pt-2">
        New here? <button type="button" className="text-primary underline" onClick={() => {
          const tabsTrigger = document.querySelector<HTMLButtonElement>('[role="tab"][value="signup"]');
          tabsTrigger?.click();
        }}>Create an account</button>
      </p>
    </form>
  );
}
